import type { ColDef, IRowNode } from "ag-grid-enterprise";

/**
 * Client-side length gate for the editable voter-list grids (EditorGridComponent and
 * PostSaveGridComponent), so a value past a column's varchar limit is flagged where the organizer
 * typed it instead of failing as a server error. The server stays authoritative
 * (VoterInformationLine length validations, BatchUpdate's rollback): this only spares the round trip.
 *
 * The limits are NOT hardcoded here. The server marks each limited colDef with `maxLength`
 * (VoterList::VoterInformationLines::ColumnDefsHelpers#max_length_def_for), and `decorate` consumes
 * and strips that marker — AG Grid must never see an unknown colDef key.
 *
 * A too-long value is never sent (the caller skips `GridEditQueue.queueChange` when `evaluate`
 * says so). The red highlight derives from the cell's CURRENT value rather than from
 * stored state, so they cannot outlive the value that caused them.
 */

export const TOO_LONG_CELL_CLASS = "ag-grid-cell--too-long";

interface TooLongCell {
  node: IRowNode;
  field: string;
  value: unknown;
  // What the server holds for this cell: the value before the first over-long edit.
  persistedValue: unknown;
}

export interface CellLengthGuardOptions {
  // The base (undecorated) colDefs currently in effect — header names and column order for messages.
  columnDefs: () => ColDef[];
  // Shows the message in the grid's status line, or `null` to clear it.
  announce: (message: string | null) => void;
}

function blank(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function valuesMatch(a: unknown, b: unknown): boolean {
  if (blank(a) && blank(b)) return true;
  return Object.is(a, b);
}

export class CellLengthGuard {
  private limits: Map<string, number> = new Map();
  private cells: Map<string, TooLongCell> = new Map();

  constructor(private readonly options: CellLengthGuardOptions) {}

  // Records each column's limit, strips the `maxLength` marker, and adds the red-cell class rule.
  // Run it BEFORE `applyErrorFieldCellClassRules`, which preserves existing `cellClassRules`.
  decorate(columnDefs: ColDef[]): ColDef[] {
    this.limits = new Map();

    return columnDefs.map((col) => {
      const { maxLength, ...rest } = col as Record<string, unknown>;
      const field = col.field;
      if (!field || typeof maxLength !== "number") return rest as ColDef;

      this.limits.set(field, maxLength);
      return {
        ...rest,
        cellClassRules: {
          [TOO_LONG_CELL_CLASS]: (params: { value?: unknown }) => this.exceeds(params.value, maxLength),
          ...((col.cellClassRules as Record<string, unknown>) ?? {}),
        },
      } as ColDef;
    });
  }

  // `send: false` means the edit must not reach the server. `oldValue` is what a failed save should
  // roll back to: the value the server holds, which differs from the event's `oldValue` when the
  // cell was left too long in between.
  evaluate(
    node: IRowNode,
    field: string,
    newValue: unknown,
    oldValue: unknown
  ): { send: boolean; oldValue: unknown } {
    const limit = this.limits.get(field);
    if (limit === undefined) return { send: true, oldValue };

    const key = this.key(node, field);
    const known = this.cells.get(key);

    if (this.exceeds(newValue, limit)) {
      this.cells.set(key, { node, field, value: newValue, persistedValue: known ? known.persistedValue : oldValue });
      this.options.announce(this.statusMessage());
      return { send: false, oldValue };
    }

    if (!known) return { send: true, oldValue };

    this.cells.delete(key);
    this.options.announce(this.statusMessage());
    // Back at the value the server already holds: nothing to save.
    if (valuesMatch(newValue, known.persistedValue)) return { send: false, oldValue };
    return { send: true, oldValue: known.persistedValue };
  }

  // Too-long values still sitting only in the grid. `GridEditQueue` overlays them on a server row so
  // saving a neighbouring cell does not wipe them.
  unsentValues(node: IRowNode): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    this.liveCells().forEach((cell) => {
      if (String(cell.node.id) === String(node.id)) values[cell.field] = cell.value;
    });
    return values;
  }

  // Message for the status line covering every cell currently too long, or null when there is none.
  statusMessage(): string | null {
    const cells = this.liveCells().sort((a, b) => this.position(a) - this.position(b));
    if (cells.length === 0) return null;

    const first = cells[0];
    if (cells.length === 1) return this.locatedMessage(first.node, first.field);
    return `${cells.length} cells are too long (maximum is ${this.limits.get(first.field)} characters)`;
  }

  // Message for one cell, for re-showing it when the cell is tapped or clicked.
  messageForCell(node: IRowNode, field: string, value: unknown): string | null {
    const limit = this.limits.get(field);
    if (limit === undefined || !this.exceeds(value, limit)) return null;

    return this.locatedMessage(node, field);
  }

  forgetNode(node: IRowNode): void {
    Array.from(this.cells.entries()).forEach(([key, cell]) => {
      if (String(cell.node.id) === String(node.id)) this.cells.delete(key);
    });
  }

  // For a wholesale row swap or cache purge: the old nodes, and anything unsent on them, are gone.
  reset(): void {
    this.cells.clear();
    this.options.announce(null);
  }

  private exceeds(value: unknown, limit: number): boolean {
    return !blank(value) && String(value).length > limit;
  }

  private key(node: IRowNode, field: string): string {
    return `${String(node.id)}\u0000${field}`;
  }

  // Drops entries whose node left the grid (removed, or purged by a server-side refresh) or whose
  // cell no longer holds a too-long value.
  private liveCells(): TooLongCell[] {
    Array.from(this.cells.entries()).forEach(([key, cell]) => {
      const limit = this.limits.get(cell.field);
      const current = (cell.node.data as Record<string, unknown> | undefined)?.[cell.field];
      if (cell.node.rowIndex === null || limit === undefined || !this.exceeds(current, limit)) this.cells.delete(key);
    });
    return Array.from(this.cells.values());
  }

  private rowNumber(node: IRowNode): number {
    return (node.rowIndex ?? 0) + 1;
  }

  private position(cell: TooLongCell): number {
    const row = this.rowNumber(cell.node);
    const column = this.options.columnDefs().findIndex((col) => col.field === cell.field);
    return row * 1000 + Math.max(column, 0);
  }

  private header(field: string): string {
    return this.options.columnDefs().find((col) => col.field === field)?.headerName ?? field;
  }

  private locatedMessage(node: IRowNode, field: string): string {
    return `Row ${this.rowNumber(node)}, ${this.header(field)}: too long (maximum is ${this.limits.get(field)} characters)`;
  }
}
